// https://github.com/vite-pwa/vite-plugin-pwa/blob/main/src/client/build/react.ts
// https://github.com/vite-pwa/create-pwa/blob/main/templates/template-react-ts/src/PWABadge.tsx
// https://stackoverflow.com/questions/73355783/proper-way-to-setup-firebase-messaging-service-worker-with-vite

import {useEffect, useRef, useState} from "react";
import type {RegisterSWOptions} from "virtual:pwa-register";
import {registerSW} from "virtual:pwa-register";

export type {RegisterSWOptions};

// The period for checking for service worker updates.
const SW_UPDATE_PERIOD = 60 * 60 * 1000; // 1 hour

/**
 * Register a periodic service worker update.
 * We expect the user to be using the app for a long time without reloading,
 * we want to call update() on an interval.
 * https://vite-pwa-org.netlify.app/guide/periodic-sw-updates.html
 * https://github.com/vite-pwa/create-pwa/blob/main/templates/template-react-ts/src/PWABadge.tsx
 */
const registerPeriodicSWUpdate = (
  period: number,
  swUrl: string,
  swRegistration: ServiceWorkerRegistration,
) => {
  return setInterval(async () => {
    // If the user is offline, don't check for updates.
    if ("onLine" in navigator && !navigator.onLine) return;

    // Check for exist service worker to update.
    const resp = await fetch(swUrl, {
      cache: "no-store",
      headers: {
        "cache": "no-store",
        "cache-control": "no-cache",
      },
    });

    // If the service worker is available, try to update it.
    // The update() method of the ServiceWorkerRegistration interface attempts to update the service worker.
    // It fetches the worker's script URL, and if the new worker is not byte-by-byte identical to the current worker,
    // it installs the new worker. The fetch of the worker bypasses any browser caches if the previous fetch occurred over 24 hours ago.
    if (resp?.status === 200) {
      try {
        // Verify the registration is still valid before attempting update
        // to avoid InvalidStateError when registration becomes unregistered
        if (
          swRegistration.installing ||
          swRegistration.waiting ||
          swRegistration.active
        ) {
          await swRegistration.update();
        }
      } catch (error) {
        // Log the error but don't throw - this is a background operation
        // and we don't want to break the app if the service worker update fails
        console.error("Failed to update service worker:", error);
      }
    }
  }, period);
};

/**
 * Register a service worker and handle updates and errors.
 */
export function useRegisterServiceWorker(options: RegisterSWOptions = {}) {
  const {onNeedRefresh, onOfflineReady, onRegisterError} = options;

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  const [updateServiceWorker] = useState(() => {
    return registerSW({
      // Checks for updates of the registered service worker.
      // https://vite-pwa-org.netlify.app/guide/auto-update.html
      // https://developer.chrome.com/docs/workbox/modules/workbox-window
      // https://developer.chrome.com/docs/workbox/handling-service-worker-updates
      immediate: true,

      onOfflineReady() {
        setOfflineReady(true);
        onOfflineReady?.();
      },
      onNeedRefresh() {
        setNeedRefresh(true);
        onNeedRefresh?.();
      },
      onRegisterError,
      onRegisteredSW(
        swUrl: string,
        swRegistration: ServiceWorkerRegistration | undefined,
      ) {
        // Register a periodic service worker update.
        // If the service worker is already activated, register the periodic update.
        if (swRegistration?.active?.state === "activated") {
          intervalIdRef.current = registerPeriodicSWUpdate(
            SW_UPDATE_PERIOD,
            swUrl,
            swRegistration,
          );

          // If the service worker is installing, wait for it to be activated.
        } else if (swRegistration?.installing) {
          swRegistration.installing.addEventListener(
            "statechange",
            (e: Event) => {
              const sw = e.target as ServiceWorker;

              // If the service worker is activated, register the periodic update.
              if (sw.state === "activated")
                intervalIdRef.current = registerPeriodicSWUpdate(
                  SW_UPDATE_PERIOD,
                  swUrl,
                  swRegistration,
                );
            },
          );
        }
      },
    });
  });

  useEffect(() => {
    return () => {
      // Clear the interval when the component is unmounted.
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, []);

  return {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  };
}
